/**
 * Column-type helpers for the master surface.
 *
 * **This is a deliberate mirror of `3mote-be-new/src/lib/status-types.ts`.
 * Change one, change both.** The BE stays canonical — it re-resolves on
 * every write and rejects a mismatch — but the master board needs the same
 * answer locally so an optimistic drop lands where the server will put it,
 * with no visible snap-back. Same arrangement as the time-entry `snap()`
 * math (`src/features/time/lib/snap.ts`).
 *
 * Why a type at all: column identity used to be the status NAME, so a
 * project's custom `Testing` column collapsed into the master board's
 * Inbox bucket — a WRONG label, not a missing one. Identity is now
 * `columnType`, and the mapping back to a specific status row lives here so
 * the resolution call sites cannot drift apart.
 */

import type { ProjectStatusType } from '@/api/types-bridge'

export type { ProjectStatusType }

/**
 * The five types in canonical board order (left → right on the Kanban).
 * Drives the settings picker's option order — the ordering is the thing
 * that makes the picker legible, so it is declared once here rather than
 * re-listed per surface.
 */
export const PROJECT_STATUS_TYPES_IN_BOARD_ORDER = [
  'inbox',
  'up_next',
  'today',
  'review',
  'done',
] as const satisfies readonly ProjectStatusType[]

/**
 * Canonical (English, BE wire) column name per type. These are lookup
 * keys, NOT display strings — the master board renders them through
 * `t(COLUMN_NAME_I18N_KEYS[name])`.
 */
export const CANONICAL_STATUS_NAME_BY_TYPE: Readonly<Record<ProjectStatusType, string>> = {
  inbox: 'Inbox',
  up_next: 'Up Next',
  today: 'Today',
  review: 'Review',
  done: 'Done',
}

/** Inverse of {@link CANONICAL_STATUS_NAME_BY_TYPE}. */
export const STATUS_TYPE_BY_CANONICAL_NAME: Readonly<Record<string, ProjectStatusType>> = {
  Inbox: 'inbox',
  'Up Next': 'up_next',
  Today: 'today',
  Review: 'review',
  Done: 'done',
}

/** i18n key per type, in the `common` namespace. */
export const STATUS_TYPE_I18N_KEYS: Readonly<
  Record<
    ProjectStatusType,
    | 'projects.statuses.columnType.inbox'
    | 'projects.statuses.columnType.upNext'
    | 'projects.statuses.columnType.today'
    | 'projects.statuses.columnType.review'
    | 'projects.statuses.columnType.done'
  >
> = {
  inbox: 'projects.statuses.columnType.inbox',
  up_next: 'projects.statuses.columnType.upNext',
  today: 'projects.statuses.columnType.today',
  review: 'projects.statuses.columnType.review',
  done: 'projects.statuses.columnType.done',
}

/**
 * Types a time-derived card (event / announcement) may occupy. These
 * kinds are placed by their time window rather than dragged, so only the
 * three types that model "not yet / now / finished" are valid
 * destinations. Mirrors `EVENT_BEARING_STATUS_TYPES` on the BE, and
 * replaces the old name-based `{Up Next, Today, Done}` check that broke
 * on any rename.
 */
export const EVENT_BEARING_STATUS_TYPES = [
  'up_next',
  'today',
  'done',
] as const satisfies readonly ProjectStatusType[]

export function isEventBearingStatusType(value: ProjectStatusType): boolean {
  return (EVENT_BEARING_STATUS_TYPES as readonly ProjectStatusType[]).includes(value)
}

/** The minimum a status row needs to expose to be resolvable. */
export interface ResolvableStatus {
  readonly id: string
  readonly name: string
  readonly position: number
  readonly columnType: ProjectStatusType
}

/**
 * Pick the single status a given type resolves to on one project.
 *
 * A project may legitimately hold more than one status of a type — the
 * whole point of the feature is that `Review` and a custom `Testing` can
 * both be `review`-typed — so "the review column" needs a deterministic
 * answer.
 *
 * Rule: prefer the status whose NAME is the canonical name for that type;
 * otherwise the LOWEST `position` of that type. So a project with
 * `Review` + `Testing` receives master-board drops into `Review`, while a
 * project that renamed its review column to `Testing` receives them into
 * `Testing` — in both cases the answer is stable and explainable.
 *
 * Orders by `position` explicitly rather than trusting array order or
 * assuming positions are dense: staging holds a project whose positions
 * run 0,1,2,3,5.
 *
 * Returns `null` when the project has no status of that type. The BE
 * forbids deleting the last status of a type, so this should be
 * unreachable in practice — but it is returned rather than thrown so the
 * caller can surface its own "couldn't resolve the destination" message
 * instead of blowing up on legacy data that predates the invariant.
 */
export function resolveStatusForType<T extends ResolvableStatus>(
  statuses: readonly T[],
  type: ProjectStatusType,
): T | null {
  const ofType = statuses.filter((s) => s.columnType === type)
  if (ofType.length === 0) return null
  if (ofType.length === 1) return ofType[0] ?? null

  const canonicalName = CANONICAL_STATUS_NAME_BY_TYPE[type]
  const canonical = ofType.find((s) => s.name === canonicalName)
  if (canonical) return canonical

  return ofType.reduce((lowest, s) => (s.position < lowest.position ? s : lowest))
}

/**
 * The master-board column a status buckets into. Replaces the old
 * name-equality bucketing, which collapsed every non-canonical NAME to
 * `Inbox`.
 */
export function bucketByType(type: ProjectStatusType): string {
  return CANONICAL_STATUS_NAME_BY_TYPE[type]
}

/**
 * Resolve a master-board column name back to the type it represents.
 * `undefined` for anything outside the canonical five — the master board
 * only ever renders those, so a miss means the caller has drifted.
 */
export function statusTypeForColumnName(columnName: string): ProjectStatusType | undefined {
  return STATUS_TYPE_BY_CANONICAL_NAME[columnName]
}
